import React from 'react';
import { Stack, TextField, Image, IconButton, ImageFit, Icon, Pivot, PivotItem, Checkbox } from '@fluentui/react';
import { UploadedImage, UnsplashImage } from './ImageUpload.state';
import { useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import IvicosStrings from 'kits/language/stringKit';

interface UploadImageViewProps {
    uploadedImage: UploadedImage | null;
    isDragging: boolean;
    unsplashImages: any[];
    selectedUnsplashImage: any;
    // unsplashImages: UnsplashImage[];
    // selectedUnsplashImage: UnsplashImage | null;
    searchQuery: string;
    loading: boolean;
    error: string | null;
    onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
    onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    onDescriptionChange: (v?: string) => void;
    removeImage: () => void;
    handleUnsplashSearch: (value?: string) => void;
    setSelectedUnsplashImage: (img: UnsplashImage | null) => void;
    newAltOfImage: string;
    setNewAltOfImage: (alt: string) => void;
    preserveAspectRatio: boolean;
    setPreserveAspectRatio: (preserve: boolean) => void;
    setUnsplashImages: (images: any[]) => void;
    setSearchQuery: (query: string) => void;
}

export const UploadImageView: React.FC<UploadImageViewProps> = (props) => {
    const {
        uploadedImage,
        isDragging,
        unsplashImages,
        selectedUnsplashImage,
        searchQuery,
        loading,
        error,
        onImageChange,
        onDragOver,
        onDragLeave,
        onDrop,
        onDescriptionChange,
        removeImage,
        handleUnsplashSearch,
        setSelectedUnsplashImage,
        newAltOfImage,
        setNewAltOfImage,
        preserveAspectRatio,
        setPreserveAspectRatio,
        setUnsplashImages,
        setSearchQuery
    } = props;
    const [editedWidget] = useEditedWidget();

    const renderUpload = () => (
        <Stack>
            <Stack
                style={{
                    border: `2px dashed ${isDragging ? '#0078d4' : '#ccc'}`,
                    padding: 20,
                    marginBottom: 10,
                    borderRadius: 4,
                    backgroundColor: isDragging ? '#f0f7ff' : '#f9f9f9',
                    cursor: 'pointer',
                    height: 100
                }}
                onClick={() => (document.getElementById('fileInput') as HTMLInputElement)?.click()}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
            >
                <input type="file" accept="image/*" id="fileInput" style={{ display: 'none' }} onChange={onImageChange} />
                <Stack horizontalAlign="center">
                    <Icon iconName="Upload" styles={{ root: { fontSize: 24 } }} />
                    <div>{IvicosStrings.personalMeetingCornerStrings.imageUpload.clickOrDragToUpload}</div>
                </Stack>
            </Stack>
        </Stack>
    );

    const renderUnsplash = () => (
        <Stack tokens={{ childrenGap: 10 }}>
            <TextField
                placeholder={IvicosStrings.personalMeetingCornerStrings.imageUpload.searchUnsplash}
                value={searchQuery}
                onChange={(_, val) => {
                    // Always update the input value
                    setSearchQuery(val || '');
                    // If cleared, clear results
                    if (!val) setUnsplashImages([]);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        handleUnsplashSearch(searchQuery);
                    }
                }}
                iconProps={{ iconName: 'Search' }}
            />
            {loading && <div>{IvicosStrings.personalMeetingCornerStrings.imageUpload.loading}</div>}
            {error && <div style={{ color: 'red' }}>{error}</div>}
            <Stack
                horizontal
                horizontalAlign="space-between"
                wrap
                tokens={{ childrenGap: 10 }}
                style={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: 320, padding: 10 }}
            >
                {unsplashImages.map((img) => (
                    <Stack
                        key={img.id}
                        onClick={() => setSelectedUnsplashImage(img)}
                        style={{
                            width: 150,
                            height: 150,
                            border: selectedUnsplashImage?.id === img.id ? '2px solid #0078d4' : '2px solid transparent',
                            borderRadius: 5,
                            overflow: 'hidden',
                            cursor: 'pointer'
                        }}
                    >
                        <Image src={img.url} imageFit={ImageFit.cover} width="100%" height="100%" />
                    </Stack>
                ))}
            </Stack>
            {selectedUnsplashImage && (
                <Stack>
                    <Stack horizontal horizontalAlign="space-between">
                        <Image src={selectedUnsplashImage.url} imageFit={ImageFit.cover} width="100%" height={100} styles={{ root: { borderRadius: 4 } }} />
                        {/* <IconButton iconProps={{ iconName: 'Cancel' }} onClick={() => setSelectedUnsplashImage(null)} /> */}
                    </Stack>
                    {/* <TextField
                        label={IvicosStrings.personalMeetingCornerStrings.imageUpload.description}
                        value={selectedUnsplashImage.description}
                        onChange={(_, val) => setSelectedUnsplashImage({ ...selectedUnsplashImage, description: val || '' })}
                        maxLength={70}
                    /> */}
                </Stack>
            )}
        </Stack>
    );

    return (
        <Stack tokens={{ childrenGap: 10 }} styles={{ root: { width: '100%', overflow: 'hidden' } }}>
            {!editedWidget?.content.imageUrl && (
                <Pivot>
                    <PivotItem headerText={IvicosStrings.personalMeetingCornerStrings.imageUpload.searchUnsplashHeader}>{renderUnsplash()}</PivotItem>
                    <PivotItem headerText={IvicosStrings.personalMeetingCornerStrings.imageUpload.upload}>{renderUpload()}</PivotItem>
                </Pivot>
            )}

            {uploadedImage && (
                <Stack tokens={{ childrenGap: 5 }}>
                    <Stack horizontal horizontalAlign="space-between">
                        <Image src={uploadedImage.previewUrl} imageFit={ImageFit.cover} width="100%" height={100} styles={{ root: { borderRadius: 4 } }} />
                        <IconButton iconProps={{ iconName: 'Cancel' }} onClick={removeImage} />
                    </Stack>
                    <TextField
                        label={IvicosStrings.personalMeetingCornerStrings.imageUpload.description}
                        value={uploadedImage.description}
                        onChange={(_, val) => onDescriptionChange(val)}
                        maxLength={70}
                    />
                </Stack>
            )}

            {editedWidget && (
                <Stack tokens={{ childrenGap: 5 }}>
                    <Stack horizontal horizontalAlign="space-between">
                        <Image src={editedWidget.content.imageUrl} imageFit={ImageFit.cover} width="100%" height={250} styles={{ root: { borderRadius: 4 } }} />
                    </Stack>
                    <TextField
                        label={IvicosStrings.personalMeetingCornerStrings.imageUpload.description}
                        value={newAltOfImage ? newAltOfImage : editedWidget.content.alt}
                        onChange={(_, val) => setNewAltOfImage(val || '')}
                        maxLength={70}
                    />
                </Stack>
            )}

            <Checkbox
                label={IvicosStrings.personalMeetingCornerStrings.createWidget.preserveAspectRatio}
                checked={preserveAspectRatio}
                onChange={(_, checked) => setPreserveAspectRatio(checked || false)}
            />
        </Stack>
    );
};
